import { Component, OnInit, Input } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { OregonWellDataService } from '../../../services/oregon-well-data.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedService } from '@shared/services/shared.service';
import { AuthService } from '@shared/auth/auth.service';
import { IInvoice } from 'app/models/invoices/invoice';
import { InvoiceService } from 'app/services/invoice-service';
import { ILab } from 'app/models/labs/lab';
import { LabService } from 'app/services/lab-service';
import { Observable } from 'rxjs/internal/Observable';
import { map, startWith } from 'rxjs/operators';
import { ISelectListItem } from 'app/models/common/select.list.item';
import { DialogAddTreatmentComponent } from 'app/components/households/treatments/add/add-treatment-dialog';
import { MatDialog } from '@angular/material/dialog';

/*
 * Edit invoice
 */
@Component({
  selector: 'invoice-edit',
  templateUrl: 'invoice-edit.html',
  styleUrls: ['./invoice-edit.scss'],
  //encapsulation: ViewEncapsulation.None
})
export class InvoiceEditComponent implements OnInit {
  @Input('invoiceId')
  invoiceId: number = 0;
  buttonText = "Add Invoice";
  loading: boolean = false;
  processing: boolean = false;
  labs: ILab[] = [];
  voucherIds: ISelectListItem[] = [];
  filteredLabs: Observable<ILab[]>;
  filteredVoucherIds: Observable<ISelectListItem[]>;

  invoice: IInvoice = {
    id: 0,
    treatmentId: 0,
    createDate: '',
    createdBy: '',
    lastUpdated: '',
    lastUpdatedBy: '',
    voucherIdentifier: '',
    voucher: undefined,
    labName: '',
    invoiceNumber: '',
    sampleDate: '',
    serviceDate: '',
    receivedDate: '',
    dueDate: '',
    approvedByCoordinator: '',
    approvedByManager: '',
    sentToFiscal: '',
    paidDate: '',
    amountDue: '',
    amountPaid: '',
    paymentComplete: false,
    pca: '',
    index: '',
    agencyObjectCode: '',
    mailCode: '',
    comments: '',
    invoiceType: ''
  };

  labsControl = new FormControl('', Validators.required);
  voucherIdsControl = new FormControl('', Validators.required);

  invoiceForm = new FormGroup({
    id: new FormControl(0),
    invoiceNumber: new FormControl('', Validators.required),
    approvedByCoordinator: new FormControl(new Date()),
    sampleDate: new FormControl({ value: new Date(), disabled: true }),
    serviceDate: new FormControl(new Date()),
    approvedByManager: new FormControl(new Date()),
    sentToFiscal: new FormControl(new Date()),
    receivedDate: new FormControl(new Date()),
    index: new FormControl(''),
    pca: new FormControl(''),
    agencyObjectCode: new FormControl(''),
    mailCode: new FormControl(''),
    dueDate: new FormControl(new Date()),
    paidDate: new FormControl(new Date('')),
    amountPaid: new FormControl(''),
    amountDue: new FormControl(''),
    comments: new FormControl(''),
    invoiceType: new FormControl(''),
    paymentComplete: new FormControl({ value: false, disabled: false }),
    treatmentId: new FormControl(''),
  });

  constructor(public oregonWellDataService: OregonWellDataService,
    public invoiceService: InvoiceService, public labService: LabService,
    private toastr: ToastrService, private route: ActivatedRoute,
    public dialog: MatDialog,
    private sharedService: SharedService, private router: Router, public authService: AuthService) {

    this.loading = true;
    let routeId = this.route.snapshot.paramMap.get("id");

    if (routeId != null && routeId != undefined && routeId.length > 0) {
      this.invoiceId = Number(this.route.snapshot.paramMap.get("id"));
    }
    if (this.invoiceId == null || this.invoiceId < 0) {
      this.invoiceId = 0;
    }
  }

  ngOnInit(): void {
    this.loadInvoice();
  }

  private _filterLabs(value: string): ILab[] {
    const filterValue = value.toLowerCase();
    return this.labs.filter(option => option.labName.toLowerCase().includes(filterValue));
  }

  private _filterVoucherIds(value: string): ISelectListItem[] {
    const filterValue = value.toLowerCase();
    return this.voucherIds.filter(option => option.text.toLowerCase().includes(filterValue));
  }

  loadInvoice() {
    this.loading = true;

    this.labService.getLabs().subscribe(
      result => {
        this.labs = result;
        this.filteredLabs = this.labsControl.valueChanges.pipe(
          startWith(''),
          map(value => this._filterLabs(value || '')),
        );
        this.processing = false;
      },
      error => {
        this.toastr.error(error.message);
        this.processing = false;
      }
    );

    this.invoiceService.getVoucherIds().subscribe(
      result => {
        this.voucherIds = result;
        this.filteredVoucherIds = this.voucherIdsControl.valueChanges.pipe(
          startWith(''),
          map(value => this._filterVoucherIds(value || '')),
        );
        this.processing = false;
      },
      error => {
        this.toastr.error(error.message);
        this.processing = false;
      }
    );

    if (this.invoiceId <= 0) {
      this.loading = false;
      this.setFormValues();
      return;
    }

    this.buttonText = "Update Invoice";
    this.invoiceService.getInvoiceById(this.invoiceId).subscribe(
      result => {
        this.invoice = result;
        this.setFormValues();
        this.loading = false;
      },
      error => {
        this.loading = false;
      }
    );
  }

  onSubmit() {
    // #region validation

    if (!this.invoiceForm.valid) {
      this.toastr.error("Please fix form validation issues");
      return;
    }

    if (this.invoiceForm.value.invoiceNumber == null
      || this.invoiceForm.value.invoiceNumber == '') {
      this.toastr.error("Invoice Number is required");
      return;
    }

    if (this.labsControl.value == null
      || this.labsControl.value == ''
      || this.labsControl.value == '0') {
      this.toastr.error("Lab Name is required");
      return;
    }

    if (this.voucherIdsControl.value == null
      || this.voucherIdsControl.value == ''
      || this.voucherIdsControl.value == '0') {
      this.toastr.error("Voucher Identifier is required");
      return;
    }

    // #endregion

    this.processing = true;
    this.getFormValues();

    if (this.invoice.id > 0) {

      // save
      this.invoiceService.saveVoucherInvoice(this.invoice).subscribe(
        result => {
          this.invoiceId = result;
          this.loadInvoice();
          this.toastr.success("Invoice Updated");
          this.processing = false;
        },
        error => {
          this.processing = false;
        }
      );
    }
    else {

      // add
      this.invoiceService.saveVoucherInvoice(this.invoice).subscribe(
        result => {
          this.invoiceId = result;
          this.toastr.success("Invoice Updated");
          this.processing = false;
        },
        error => {
          this.processing = false;
        },
        () => {
          this.router.navigate(['invoices/edit/' + this.invoiceId]);
        }
      );
    }
  }

  editTreatment() {
    if (this.invoice.treatmentId <= 0) {
      this.toastr.error("No treatment associated with this invoice");
      return;
    }

    const dialogRef = this.dialog.open(DialogAddTreatmentComponent, {
      width: '90%',
      data: {
        treatmentId: this.invoice.treatmentId.toString()
      },
    });
  }

  getFormValues() {
    this.invoice.id = this.invoiceId;
    this.invoice.invoiceNumber = this.invoiceForm.get('invoiceNumber').value;
    this.invoice.labName = this.labsControl.value;
    this.invoice.voucherIdentifier = this.voucherIdsControl.value;
    this.invoice.approvedByCoordinator = this.invoiceForm.get('approvedByCoordinator').value;
    this.invoice.sampleDate = this.invoiceForm.get('sampleDate').getRawValue();
    this.invoice.serviceDate = this.invoiceForm.get('serviceDate').value;
    this.invoice.approvedByManager = this.invoiceForm.get('approvedByManager').value;
    this.invoice.sentToFiscal = this.invoiceForm.get('sentToFiscal').value;
    this.invoice.receivedDate = this.invoiceForm.get('receivedDate').value;
    this.invoice.index = this.invoiceForm.get('index').value;
    this.invoice.pca = this.invoiceForm.get('pca').value;
    this.invoice.agencyObjectCode = this.invoiceForm.get('agencyObjectCode').value;
    this.invoice.mailCode = this.invoiceForm.get('mailCode').value;
    this.invoice.dueDate = this.invoiceForm.get('dueDate').value;
    this.invoice.paidDate = this.invoiceForm.get('paidDate').value;
    this.invoice.amountPaid = this.invoiceForm.get('amountPaid').value;
    this.invoice.amountDue = this.invoiceForm.get('amountDue').value;
    this.invoice.comments = this.invoiceForm.get('comments').value;
    this.invoice.paymentComplete = this.invoiceForm.get('paymentComplete').value;
    this.invoice.invoiceType = this.invoiceForm.get('invoiceType').value;
  }

  setFormValues() {
    this.invoice.id = this.invoiceId;
    this.invoiceForm.get('invoiceNumber').setValue(this.invoice.invoiceNumber);
    this.labsControl.setValue(this.invoice.labName);
    this.voucherIdsControl.setValue(this.invoice.voucherIdentifier);
    this.invoiceForm.get('index').setValue(this.invoice.index);
    this.invoiceForm.get('pca').setValue(this.invoice.pca);
    this.invoiceForm.get('agencyObjectCode').setValue(this.invoice.agencyObjectCode);
    this.invoiceForm.get('mailCode').setValue(this.invoice.mailCode);
    this.invoiceForm.get('amountPaid').setValue(this.invoice.amountPaid);
    this.invoiceForm.get('amountDue').setValue(this.invoice.amountDue);
    this.invoiceForm.get('comments').setValue(this.invoice.comments);
    this.invoiceForm.get('paymentComplete').setValue(this.invoice.paymentComplete);
    this.invoiceForm.get('invoiceType').setValue(this.invoice.invoiceType);

    // #region Dates

    if (this.invoice.approvedByCoordinator == null
      || this.invoice.approvedByCoordinator.toString() == ''
      || this.invoice.approvedByCoordinator.toString() == "Invalid Date") {

      this.invoiceForm.get('approvedByCoordinator').setValue(null);
    }
    else {
      this.invoiceForm.get('approvedByCoordinator').setValue(new Date(this.invoice.approvedByCoordinator));
    }

    if (this.invoice.sampleDate == null
      || this.invoice.sampleDate.toString() == ''
      || this.invoice.sampleDate.toString() == "Invalid Date") {

      this.invoiceForm.get('sampleDate').setValue(null);
    }
    else {
      this.invoiceForm.get('sampleDate').setValue(new Date(this.invoice.sampleDate));
    }

    if (this.invoice.serviceDate == null
      || this.invoice.serviceDate.toString() == ''
      || this.invoice.serviceDate.toString() == "Invalid Date") {

      this.invoiceForm.get('serviceDate').setValue(null);
    }
    else {
      this.invoiceForm.get('serviceDate').setValue(new Date(this.invoice.serviceDate));
    }

    if (this.invoice.sentToFiscal == null
      || this.invoice.sentToFiscal.toString() == ''
      || this.invoice.sentToFiscal.toString() == "Invalid Date") {

      this.invoiceForm.get('sentToFiscal').setValue(null);
    }
    else {
      this.invoiceForm.get('sentToFiscal').setValue(new Date(this.invoice.sentToFiscal));
    }

    if (this.invoice.approvedByManager == null
      || this.invoice.approvedByManager.toString() == ''
      || this.invoice.approvedByManager.toString() == "Invalid Date") {

      this.invoiceForm.get('approvedByManager').setValue(null);
    }
    else {
      this.invoiceForm.get('approvedByManager').setValue(new Date(this.invoice.approvedByManager));
    }

    if (this.invoice.receivedDate == null
      || this.invoice.receivedDate.toString() == ''
      || this.invoice.receivedDate.toString() == "Invalid Date") {

      this.invoiceForm.get('receivedDate').setValue(null);
    }
    else {
      this.invoiceForm.get('receivedDate').setValue(new Date(this.invoice.receivedDate));
    }

    if (this.invoice.dueDate == null
      || this.invoice.dueDate.toString() == ''
      || this.invoice.dueDate.toString() == "Invalid Date") {

      this.invoiceForm.get('dueDate').setValue(null);
    }
    else {
      this.invoiceForm.get('dueDate').setValue(new Date(this.invoice.dueDate));
    }

    if (this.invoice.paidDate == null
      || this.invoice.paidDate.toString() == ''
      || this.invoice.paidDate.toString() == "Invalid Date") {

      this.invoiceForm.get('paidDate').setValue(null);
    }
    else {
      this.invoiceForm.get('paidDate').setValue(new Date(this.invoice.paidDate));
    }

    // #endregion
  }

  voucherIdentifierChange(selectedValue: string) {
    if (selectedValue == '') {
      return;
    }

    this.invoiceService.getMostRecentNitrateTestDate(selectedValue).subscribe(
      result => {
        this.invoiceForm.get('sampleDate').setValue(new Date(result));
        this.processing = false;
      },
      error => {
        this.toastr.error(error.message);
        this.processing = false;
      }
    );
  }
}